import { Box, Loader, Group, Menu } from '@mantine/core';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { FamilyIndividualToggle } from 'src/non-financial-data/v2/FamilyIndividualToggle';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SearchPill } from 'src/UI/generate-flow/SearchPill';
import classes from './ClientsPage.module.css';
import { IconChevronDown, IconSearch, IconX } from '@tabler/icons-react';
import UnderlineTabsDeprecated from '../UI/page-nav/UnderlineTabsDeprecated';
import { ClientsTable } from './ClientsTable';
import { NewClientsTable } from './NewClientsTable';
import { FamiliesTable } from './FamiliesTable';
import { ClientTabs, useClientTab } from './hooks/useClientTab';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { FamilyClientsTable } from './components/FamilyClientsTable';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClientsContext } from 'src/clients/context/ClientsContext';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSessionStorage } from '@mantine/hooks';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { useClients } from './hooks/useClients';
import { useClientsPaginated } from './hooks/useClientsPaginated';
import { ClientsEmptyState } from './components/ClientsEmptyState';
import { useFlags } from 'src/feature-flags/FlagsHook';

export default function ClientsPage() {
  const { currentTab, setTab } = useClientTab();
  const { id } = useParams<{ id?: string }>();
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();
  const { startClientCreation, startFamilyCreation } = useClientsContext();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const handledCreate = useRef(false);
  const {
    flagEnabledSiestaSteering,
    flagEnabledIndividualDetailsRework,
    flagEnabledFamilyFlowFrontend,
    flagEnabledPaginatedClients,
  } = useFlags();
  const { setSidebarFullScreen, setSidebarContent, setPreventSidebarCollapse } =
    useAppContainerContext();

  const cq = useContainerQuery();
  const responsivePadding = cq({ base: 'xs', lg: 'sm', xl: 'md' });

  const clientsFilter = getClientsFilter(currentUser);
  const unpaginatedClients = useClients(clientsFilter, { enabled: !flagEnabledPaginatedClients });
  const paginatedClients = useClientsPaginated(clientsFilter, {
    enabled: flagEnabledPaginatedClients,
  });
  const clientsExist = hasClients(
    flagEnabledPaginatedClients,
    paginatedClients,
    unpaginatedClients,
  );
  const isClientsLoading = flagEnabledPaginatedClients
    ? paginatedClients.isLoading
    : unpaginatedClients.isLoading;
  const [emptyStateDismissed, setEmptyStateDismissed] = useSessionStorage({
    key: 'clients-empty-state-dismissed',
    defaultValue: false,
  });

  const showEmptyState =
    flagEnabledSiestaSteering && !isClientsLoading && !clientsExist && !emptyStateDismissed;

  const dismissEmptyState = () => {
    setEmptyStateDismissed(true);
    setSidebarFullScreen(false);
    setSidebarContent(null);
  };

  const handleAddManually = () => {
    dismissEmptyState();
    startClientCreation();
  };

  useLayoutEffect(() => {
    if (!showEmptyState) return;

    setSidebarFullScreen(true);
    setPreventSidebarCollapse(true);
    setSidebarContent(
      <ClientsEmptyState onAddManually={handleAddManually} onDismiss={dismissEmptyState} />,
    );

    return () => {
      setSidebarFullScreen(false);
      setPreventSidebarCollapse(false);
      setSidebarContent(null);
    };
  }, [showEmptyState]);

  useEffect(() => {
    if (searchParams.get('create') === 'true' && !handledCreate.current) {
      handledCreate.current = true;
      const firstName = searchParams.get('first_name') ?? undefined;
      const lastName = searchParams.get('last_name') ?? undefined;
      const emailAddress = searchParams.get('email_address') ?? undefined;
      const prefill =
        firstName || lastName || emailAddress ? { firstName, lastName, emailAddress } : undefined;
      startClientCreation(prefill);
      setSearchParams(
        prev => {
          prev.delete('create');
          prev.delete('first_name');
          prev.delete('last_name');
          prev.delete('email_address');
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, startClientCreation, setSearchParams]);

  const tabContent = getTabContent(
    currentUser,
    currentTab,
    id,
    startClientCreation,
    flagEnabledIndividualDetailsRework,
    searchTerm,
    flagEnabledPaginatedClients,
  );

  if (isCurrentUserLoading) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Loader />
      </Box>
    );
  }

  if (flagEnabledIndividualDetailsRework) {
    return (
      <Box className={classes.familyFlowContent}>
        <Group justify="space-between" mb="md" align="center">
          <Group gap="sm" align="center" style={{ flex: searchOpen ? 1 : undefined }}>
            <IconButton
              size={48}
              bg="brand.0"
              c="brand.7"
              style={{ flexShrink: 0 }}
              onClick={() => {
                if (searchOpen) setSearchTerm('');
                setSearchOpen(o => !o);
              }}
            >
              {searchOpen ? <IconX size={24} /> : <IconSearch size={24} />}
            </IconButton>

            {searchOpen ? (
              <SearchPill value={searchTerm} onChange={setSearchTerm} placeholder="Search..." />
            ) : (
              <FamilyIndividualToggle
                value={
                  (currentTab ?? ClientTabs.Families) === ClientTabs.Families
                    ? 'family'
                    : 'individual'
                }
                onChange={view =>
                  setTab(view === 'family' ? ClientTabs.Families : ClientTabs.Clients)
                }
                familyLabel="Families"
                individualLabel="Individuals"
                chosenSegmentPaddingX={{ left: 41.5, right: 37.5 }}
              />
            )}
          </Group>
          {!searchOpen && (
            <Menu
              position="bottom-end"
              offset={8}
              width="target"
              styles={{
                dropdown: {
                  borderRadius: 16,
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.8)',
                  overflow: 'hidden',
                  padding: 0,
                },
              }}
            >
              <Menu.Target>
                <Button
                  variant="primary"
                  px={undefined}
                  pl={16}
                  pr={12}
                  rightSection={<IconChevronDown size={24} color="white" />}
                  styles={{
                    root: { '&:active': { backgroundColor: 'var(--mantine-color-brand-8)' } },
                    section: { marginInlineStart: 8 },
                  }}
                >
                  Add family or individual
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  fz="sm"
                  fw={FONT_WEIGHTS.medium}
                  c="black"
                  onClick={
                    flagEnabledFamilyFlowFrontend
                      ? () => navigate('/clients/families/new')
                      : startFamilyCreation
                  }
                >
                  Add family
                </Menu.Item>
                <Menu.Item
                  fz="sm"
                  fw={FONT_WEIGHTS.medium}
                  c="black"
                  onClick={() => startClientCreation()}
                >
                  Add individual
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
        {tabContent}
      </Box>
    );
  }

  return (
    <Box p={responsivePadding}>
      <UnderlineTabsDeprecated
        value={currentTab}
        onChange={value => {
          if (value != null) {
            setTab(value as ClientTabs);
          }
        }}
      >
        <UnderlineTabsDeprecated.List>
          <UnderlineTabsDeprecated.Tab value={ClientTabs.Clients}>
            Clients
          </UnderlineTabsDeprecated.Tab>
          <UnderlineTabsDeprecated.Tab value={ClientTabs.Families}>
            Families
          </UnderlineTabsDeprecated.Tab>
        </UnderlineTabsDeprecated.List>

        <Box mt="sm">{tabContent}</Box>
      </UnderlineTabsDeprecated>
    </Box>
  );
}

export function getClientsFilter(currentUser: { id?: string; userRole: string } | undefined): {
  advisorUserId?: string;
} {
  if (!currentUser?.id || ['SYSTEM_ADMIN', 'SERVICE_ADMIN'].includes(currentUser.userRole)) {
    return {};
  }
  return { advisorUserId: currentUser.id };
}

export function hasClients(
  flagEnabledPaginatedClients: boolean,
  paginated: { total: number },
  legacy: { clients: unknown[] },
): boolean {
  return flagEnabledPaginatedClients ? paginated.total > 0 : legacy.clients.length > 0;
}

function getTabContent(
  currentUser: { id?: string; userRole: string } | undefined,
  currentTab: string | undefined,
  id: string | undefined,
  startClientCreation: () => void,
  flagEnabledIndividualDetailsRework?: boolean,
  searchTerm?: string,
  flagEnabledPaginatedClients?: boolean,
) {
  if (!currentUser?.id) return null;

  const filters = getClientsFilter(currentUser);

  if (currentTab === ClientTabs.Clients) {
    if (flagEnabledIndividualDetailsRework) {
      return (
        <NewClientsTable
          filters={filters}
          searchTerm={searchTerm}
          paginated={flagEnabledPaginatedClients}
        />
      );
    }
    return (
      <ClientsTable
        filters={filters}
        paginated={flagEnabledPaginatedClients}
        primaryButton={{
          label: 'Add client',
          onClick: startClientCreation,
        }}
      />
    );
  }
  if (currentTab === ClientTabs.Families) {
    if (id != null) {
      return <FamilyClientsTable id={id} />;
    }

    return <FamiliesTable filters={filters} searchTerm={searchTerm} />;
  }
  return null;
}
